<template>
  <div>
    <page-header
      has-return
      :page="pageConfig"
      :actions="pageActionsConfig"
      left-side-class="col-12 col-lg-8"
      right-side-class="col-12 col-lg-4 d-flex align-items-center justify-content-end"
      @onReturn="() => $router.back()"
    />

    <div v-if="isCatalogFormVisible" class="bg-gray px-4 py-3 rounded my-3">
      <div class="mb-3">
        <icon-view
          :has-border="false"
          icon="fas fa-file-plus text-orange"
          icon-size="17px"
          title="Añadir Material o Equipo"
          variant="custom-icon-view"
          icon-background="white"
        />
      </div>

      <validation-observer ref="catalogValidator" novalidate tag="form">
        <div class="row">
          <div class="col-12 col-md">
            <validation-provider
              v-slot="{ errors }"
              mode="eager"
              rules="required"
            >
              <custom-drop-down
                label="Categoría:"
                fieldkey="id"
                fieldname="name"
                placeholder="Seleccione"
                class="w-100 mb-4 bold-label"
                custom-error-msg="Favor seleccione una materia"
                v-model="model.category"
                :options="catalog.collections.categories"
                :error="errors[0]"
              />
            </validation-provider>
          </div>
          <div class="col-12 col-md">
            <validation-provider
              v-slot="{ errors }"
              mode="eager"
              rules="required"
            >
              <custom-input
                v-model="model.name"
                custom-error-msg="El titulo de libro es requerido"
                :error="errors[0]"
                label="Nombre del Material o Equipo:"
                placeholder="Nombre del Material o Equipo"
              />
            </validation-provider>
          </div>
          <div
            class="col-12 col-md-2 mt-3 mt-md-0"
            :class="{ 'px-0': !isMobile }"
          >
            <div
              class="d-flex align-items-center h-100"
              :class="{ 'flex-column': isMobile }"
            >
              <div :class="{ 'w-100 mb-2': isMobile }">
                <action-button
                  id="btnCancel"
                  i="fas fa-times-circle"
                  text="Cancelar"
                  :variant="
                    isMobile
                      ? 'outline-danger mr-2 w-100'
                      : 'outline-danger mr-2 mt-2'
                  "
                  @click="cancel"
                />
              </div>
              <div :class="{ 'w-100': isMobile }">
                <action-button
                  id="btnSave"
                  i="fas fa-save"
                  text="Guardar"
                  :variant="isMobile ? 'success w-100' : 'success mt-2'"
                  @click="addCatalog"
                />
              </div>
            </div>
          </div>
        </div>
      </validation-observer>
    </div>

    <filter-manager
      ref="filterRef"
      v-model="catalog.filter"
      actions-container-class="col-sm-1 col-12 text-right"
      :filters="filterConfig"
      @search="handleSearch"
    />
    <custom-separator class="my-4" text="Resultados" />

    <div v-if="hasResults">
      <result-row v-for="(item, key) in results" :key="key" :item="item" />
      <pagination
        v-if="pagination.totalItemCount > pagination.pageSize"
        :data="pagination"
        :page-size="pagination.pageSize"
        @pagination-go-page="onSearch"
      />
    </div>
    <custom-result-not-found
      v-else
      class="mt-4 row pt-3"
      text="No hay Información Disponible"
    />
  </div>
</template>

<script>
// libs
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import Pagination from 'pagination';
import CustomResultNotFound from 'custom-result-not-found';
import IconView from 'icon-view';
import CustomDropDown from 'custom-drop-down';
import CustomInput from 'CustomInput';
import ActionButton from 'ActionButton';

//local components
import ResultRow from './components/ResultRow.vue';

// services
import inventoryCatalogService from '@/services/api/materialsAndEquipments.api';
// mixins
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import storeMixin from '@quisit/mipe-utils/mixins/storeGenerator';

const MATERIALS_AND_EQUIPMENTS_MANAGEMENT_ROUTE_NAME =
  'materials-and-equipments-catalog-management';
const DEFAULT_PAGE_SIZE = 10;

export default {
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    Pagination,
    CustomResultNotFound,
    IconView,
    CustomDropDown,
    CustomInput,
    ActionButton,
    ResultRow,
  },
  mixins: [alertMixin, storeMixin],
  data: () => ({
    name: 'materials-and-equipments',
    model: {
      category: null,
      name: null,
    },
    isCatalogFormVisible: false,
    catalog: {
      filter: {
        name: null,
        category: null,
      },
      collections: { categories: [] },
    },
    results: [],
    pagination: {
      count: 0,
      currentPage: 1,
      pageCount: 1,
      totalItemCount: 0,
      pageSize: DEFAULT_PAGE_SIZE,
    },
  }),
  computed: {
    hasResults() {
      return Boolean(this.results.length);
    },
    isMobile() {
      return this.$store.getters.getIsMobileDensity;
    },
    pageConfig() {
      return {
        icon: 'fas fa-user-tie',
        title: 'Materiales y Equipos',
        routes: ['Inicio', 'PEI', 'Catálogo de Materiales y Equipos'],
      };
    },
    pageActionsConfig() {
      return [
        {
          name: 'Añadir',
          icon: 'fas fa-file-plus',
          variant: 'success',
          text: 'Añadir',
          visible: true,
          click: () => (this.isCatalogFormVisible = true),
        },
      ];
    },
    filterConfig() {
      return [
        {
          name: 'category',
          component: 'CustomDropDown',
          options: this.catalog.collections.categories,
          value: this.catalog.filter.category,
          placeholder: 'Categoría',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-sm-12 col-md-6',
          classContainer: 'w-100',
        },
        {
          name: 'name',
          component: 'CustomInput',
          options: this.catalog.collections.names,
          value: this.catalog.filter.name,
          placeholder: 'Nombre del Material o Equipo',
          fieldName: 'name',
          fieldKey: 'id',
          icon: 'fa fa-search orange-icon',
          leftIcon: true,
          class: 'col-12 col-md-6',
          classContainer: 'w-100',
        },
      ];
    },
  },
  async mounted() {
    await this.init();
  },
  destroyed() {
    this.persistData();
  },
  beforeRouteEnter(_, from, next) {
    next((vm) => {
      if (from.name == MATERIALS_AND_EQUIPMENTS_MANAGEMENT_ROUTE_NAME) {
        vm.recoveryData();
        vm.handleSearch();
      } else {
        vm.shouldRecovery = false;
      }
    });
  },
  methods: {
    cancel() {
      this.cleanModel();
      this.isCatalogFormVisible = false;
    },
    cleanModel() {
      this.model = {
        category: null,
        name: null,
      };
    },
    async handleSearch() {
      await this.onSearch();
    },
    async onSearch(page = 1) {
      const { name, category } = this.catalog.filter;
      const { pageSize } = this.pagination;

      const { data } = await inventoryCatalogService.search({
        title: name,
        categoryId: category?.id,
        currentPage: page,
        pageSize,
      });

      this.results = data.data;

      this.pagination = {
        pageSize: DEFAULT_PAGE_SIZE,
        totalItemCount: data.totalItemCount,
        pageCount: data.pageCount,
        currentPage: data.currentPage,
        count: data.count,
      };
    },
    async init() {
      await this.getCategories();
      await this.handleSearch();
    },
    async getCategories() {
      const { data } = await inventoryCatalogService.getCategories();
      this.catalog.collections.categories = data;
    },
    async addCatalog() {
      const valid = await this.$refs.catalogValidator.validate();

      if (!valid) {
        this.ShowToast(
          'Alerta',
          'Por favor, completa los campos requeridos.',
          'error'
        );
        return;
      }

      try {
        const payload = {
          name: this.model.name,
          categoryId: this.model.category?.id,
        };
        await inventoryCatalogService.add(payload);
        this.ShowToast(
          'Proceso Exitoso.',
          'El catálogo ha sido agreado exitosamente.',
          'success'
        );
        this.$refs.catalogValidator.reset();
        this.cancel();
        this.handleSearch();
      } catch (err) {
        this.ShowToast(err.message, '', 'error');
      }
    },
  },
};
</script>
<style scoped lang="scss">
::v-deep(.toggle-btn) {
  display: none !important;
}
::v-deep(.col-lg-3) {
  width: 16.666667% !important;
  max-width: 16.666667% !important;
}
</style>
